// Stridsmeldinger (C2) — kanonisk, delt datamodell for NATO-meldingsformater.
// Samme modell brukes av SKADE-COP, deles i sanntid via hubben, og speiler
// GNÅ-appens stridsmeldinger (map_screen/KTS), slik at en melding ser lik ut i
// app, TOC og (senere) ATAK/CoT.
import { z } from 'zod';
/** Meldingstyper vi støtter i første omgang. */
export const ReportType = z.enum([
    'MELDING', // Forsvarets standard stridsmelding (FRA/TIL/HVEM/HVA/HVOR/NÅR/HVORLEDES)
    'SALUTE', // fiendtlig observasjon (Size/Activity/Location/Unit/Time/Equipment)
    'CONTACT', // troops-in-contact / TIC
    'SITREP', // situasjonsrapport
    'MEDEVAC', // sanitetsevakuering (forenklet 9-liner)
    'UXO', // udetonert ammunisjon / EOD-rapport (forenklet 9-liner)
    'CBRN', // kjemisk/biologisk/radiologisk/nukleær hendelse (CBRN-1-nær)
]);
export const REPORT_FIELDS = {
    // Forsvarets standard stridsmelding. DTG settes automatisk ved sending.
    // Doktrinen krever STORE bokstaver på HIMMELRETNINGER, STEDSNAVN, KODENAVN og
    // ordet «IKKE». Systemet gjør nå to av fire selv: `stridsEmphasis()` i
    // stridsmelding.ts versaliserer HIMMELRETNINGER og «IKKE» ved sending, for alle
    // meldingstyper. STEDSNAVN og KODENAVN kan ikke utledes av teksten (se
    // `doktrineHint()` i apps/cop/src/tools/report-composer.ts for hvorfor og hva
    // som må til for å lukke resten), så de skrives fortsatt av operatøren.
    MELDING: [
        { key: 'fra', label: 'FRA (avsender)' },
        { key: 'til', label: 'TIL (mottaker)' },
        { key: 'hvem', label: 'HVEM (hvem så du)' },
        { key: 'hva', label: 'HVA (hendelse)', long: true },
        { key: 'hvor', label: 'HVOR (sted)' },
        { key: 'naar', label: 'NÅR (tidspunkt hendelsen inntraff)' },
        { key: 'hvorledes', label: 'HVORLEDES (detaljer)', long: true },
    ],
    // SALUTE = Size · Activity · Location · Unit · Time · Equipment. Rekkefølgen
    // under FØLGER akronymet. Et skjema som bryter rekkefølgen tvinger operatøren
    // til å oversette mellom det han har lært utenat og det han ser på skjermen —
    // og det er nettopp under tidspress den oversettelsen feiler.
    //
    // FUNN (UX-revisjon): `location` (L) og `time` (T) FANTES IKKE. Det er ikke
    // kosmetikk, det er to reelle hull:
    //
    //   * Uten L kunne observasjonsstedet bare utledes av kartmarkøren. Men
    //     markøren er der MELDINGEN ble plassert, ikke nødvendigvis der fienden
    //     var: å observere en kolonne på 1200 m avstand er normalen, ikke
    //     unntaket. «Der jeg satte markøren» og «der fienden er» er to ulike ting,
    //     og bare den siste er et mål.
    //   * Uten T var eneste tidsangivelse meldingens `tsMs`. `tsMs` er NÅR
    //     MELDINGEN BLE SENDT — ikke når observasjonen skjedde. En observasjon
    //     gjort 0740 og meldt 0912 (radioskygge, fremrykning, batteribytte) ble
    //     dermed lest i TOC som fersk. DET er hele grunnen til at feltet må
    //     finnes: sendetid ≠ observasjonstid, og forskjellen avgjør om et spor
    //     fortsatt er handlingsrelevant.
    //
    // Derfor autoutfylles begge ved åpning av skjemaet, redigerbart: `time` med
    // nå-tid som DTG, `location` med MGRS fra kartpunktet når meldingen åpnes på
    // et punkt. Systemet skal fylle det det VET, og operatøren skal kunne rette
    // det til det han SÅ. Se `autofyllFelter()` i
    // apps/cop/src/tools/report-composer.ts.
    //
    // U ER «UNIT» — EIERS AVGJØRELSE (Dan, 2026-07-29). Spørsmålet sto åpent
    // fordi akronymet har én U og to kandidater var i bruk: norsk
    // «Uniform/kjennetegn» kontra NATO «Unit». Det er nå avgjort: `unit` er U-et,
    // og det er det ENESTE U-feltet operatøren fyller.
    //
    // `uniform` er derfor `legacy` — ikke slettet. Grunnen er konkret og ikke
    // teoretisk: GNÅ-appen står i produksjon og sender `uniform` i sin
    // SaluteData. Hadde vi slettet feltet, ville tekst en soldat FAKTISK skrev
    // forsvunnet stille fra både tråd-visningen og STANAG-eksporten — mottakeren
    // hadde ikke sett noe hull, bare en melding uten den opplysningen. Med
    // `legacy` tilbys feltet ikke i skjemaet (ingen ny melding får to U-felt),
    // men innkommende innhold vises fortsatt. Det står til GNÅ-siden er lagt om
    // til å sende `unit`; da kan feltet fjernes helt.
    //
    // Rekkefølgen under følger akronymet S-A-L-U-T-E. `uniform` ligger BAK
    // `extra` fordi det ikke lenger er en del av akronymet — det er et vedlegg.
    //
    // GNÅ (SaluteData) sender size/activity/uniform/extra. `unit`/`time`/
    // `equipment`/`location` kom aldri fra appen — de fylles i COP.
    SALUTE: [
        { key: 'size', label: 'Størrelse (S)' },
        { key: 'activity', label: 'Aktivitet (A)' },
        { key: 'location', label: 'Sted (L)' },
        { key: 'unit', label: 'Enhet (U)' },
        { key: 'time', label: 'Tid for observasjon (T)' },
        { key: 'equipment', label: 'Materiell (E)' },
        { key: 'extra', label: 'Tillegg' },
        { key: 'uniform', label: 'Uniform/kjennetegn (fra GNÅ — utgått felt)', legacy: true },
    ],
    // GNÅ (TicData) sender `contact: true` + `note`. Uten `note` her viste
    // kontaktmeldinger NULL felt i COP.
    CONTACT: [
        { key: 'note', label: 'Kontaktmelding', long: true },
        { key: 'callsign', label: 'Kallesignal' },
        { key: 'enemy', label: 'Fiende (hva)' },
        { key: 'action', label: 'Egen handling' },
    ],
    // GNÅ (SitrepData) sender mission/comms/effectiveness (LOK-formatet).
    SITREP: [
        { key: 'mission', label: 'Oppdrag/status', long: true },
        { key: 'comms', label: 'Samband' },
        { key: 'effectiveness', label: 'Stridsevne' },
        { key: 'unit', label: 'Enhet' },
        { key: 'situation', label: 'Situasjon', long: true },
        { key: 'status', label: 'Status/behov' },
    ],
    // 9-LINJERS MEDEVAC. Nøklene MÅ være de samme som GNÅ sender (MedevacData i
    // lib/screens/reports/report_models.dart) — ellers tapes pasientdata stille.
    // Tidligere kjente COP bare 5 nøkler, hvorav 2 matchet GNÅ: HLS-merking,
    // båre-/gående-antall, hastegrad-fordeling, nasjonalitet og CBRN forsvant.
    //
    // LINJE 3 ER TRE FELT, OG DET ER RIKTIG. Linje 3 i 9-lineren er ANTALL
    // PASIENTER PER HASTEGRAD — ikke «hvilken hastegrad har hendelsen». Flere kan
    // være ikke-null samtidig: 2 HASTER + 1 RUTINE er en helt vanlig hendelse
    // (bilbombe, trafikkulykke, splintvirkning i en gruppe). Nøkkelnavnet
    // `prio_count` sier det selv: det er en TELLER.
    //
    // En UX-revisjon foreslo å gjøre linje 3 om til ETT radiovalg
    // (HASTER/PRIORITET/RUTINE). Det ville vært en regresjon, ikke en forbedring:
    // eksempelet over kunne da bare meldes som «HASTER», og rutinepasienten ville
    // forsvunnet fra meldingen. Luftevakueringen dimensjoneres på summen — den som
    // faller ut av tellingen, blir ikke hentet.
    //
    // Det som FAKTISK var galt, og som er rettet her:
    //   * Etikettene løy. «L3 Hastegrad HASTER» leses som en kategorivelger — tre
    //     radioknapper i tekstform — så feltet ble fylt med «ja»/«x»/«haster» i
    //     stedet for et antall. Nå: «L3 Antall HASTER».
    //   * Feltene var fritekst der de skal være tall. Nå `num: true`, som gir
    //     tallinnfelt og validering (ikke-negativt heltall) i komponisten.
    MEDEVAC: [
        { key: 'freq', label: 'L1 Kallesignal/frekvens' },
        { key: 'urgent', label: 'L3 Antall HASTER', num: true },
        { key: 'prio_count', label: 'L3 Antall PRIORITET', num: true },
        { key: 'routine', label: 'L3 Antall RUTINE', num: true },
        { key: 'equipment', label: 'L4 Spesialutstyr' },
        { key: 'litter', label: 'L5 Antall BÅRE', num: true },
        { key: 'ambulatory', label: 'L5 Antall GÅENDE', num: true },
        { key: 'security', label: 'L6 Sikkerhet på stedet' },
        { key: 'marking', label: 'L7 Merking av landingsplass' },
        { key: 'nationality', label: 'L8 Nasjonalitet/status' },
        { key: 'cbrn', label: 'L9 CBRN-forurensning' },
    ],
    UXO: [
        // GNÅ (UxoData) sender kind/safety_radius_m/description.
        // `safety_radius_m` er `num`: nøkkelen sier meter, og en sikkerhetsavstand
        // som «ca 300» eller «tre hundre» kan ikke tegnes som en ring i kartet eller
        // sammenlignes med avstanden til nærmeste egne. Fritekst her ga en radius
        // ingen kunne regne med.
        { key: 'kind', label: 'Type ammunisjon' },
        { key: 'safety_radius_m', label: 'Sikkerhetsavstand (m)', num: true },
        { key: 'description', label: 'Beskrivelse', long: true },
        { key: 'contact', label: 'Kallesignal/frekvens' },
        // `type` er IKKE `num` selv om ordet «antall» står i etiketten: feltet er
        // «Type/antall», altså «2 stk 155 mm granat» — sammensatt tekst, ikke et
        // tall. Å tvinge tall her ville blokkert helt korrekte meldinger.
        { key: 'type', label: 'Type/antall ammunisjon' },
        { key: 'nbc', label: 'NBC-fare (ja/nei)' },
        { key: 'resources', label: 'Ressurser truet' },
        { key: 'protective', label: 'Iverksatte tiltak' },
        { key: 'recommend', label: 'Anbefalt prioritet', long: true },
    ],
    CBRN: [
        { key: 'agent', label: 'Type (C/B/R/N) + agens' },
        { key: 'time', label: 'Tid for hendelse' },
        { key: 'wind', label: 'Vindretning/-styrke' },
        { key: 'area', label: 'Berørt område/utbredelse' },
        // «ANTATTE tap» er bevisst IKKE `num`. Ordet «antatte» inviterer til
        // intervall og forbehold — «5-10», «minst 3», «ukjent» — og det er ærligere
        // enn ett oppdiktet tall. Tvang vi tall her, ville en operatør som ikke VET
        // enten latt feltet stå tomt eller gjettet. Begge er verre enn «ukjent».
        { key: 'casualties', label: 'Antatte tap' },
        { key: 'action', label: 'Tiltak/melding', long: true },
    ],
};
/** Farge per meldingstype (kartmarkør + listeprikk). */
export const REPORT_COLOR = {
    MELDING: '#d4a03a',
    SALUTE: '#ff5b5b',
    CONTACT: '#ff8a3d',
    SITREP: '#5b9bd5',
    MEDEVAC: '#4bd15b',
    UXO: '#e0a24a',
    CBRN: '#b04bd1',
};
/**
 * HASTEGRADS-GULV per meldingstype — ÉN kilde (§3) for «hva skal øyet treffe
 * først».
 *
 * ── HVORFOR DEN FINNES ─────────────────────────────────────────────────────
 * UX-gjennomgang 2026-08-18, funn **F8** («ingen synlig hastegrad», MIDDELS):
 *
 *   «En CONTACT/TIC eller MEDEVAC ser i loggen ut som en hvilken som helst
 *   chatlinje. Kritisk og rutine er visuelt likestilt. Øyet får ingen hjelp til
 *   å prioritere. Konsekvens: det som haster kan drukne i det som ikke gjør
 *   det.»
 *
 * Før denne tabellen fantes hastegrad BARE på RFI og forsynings-anmodning, og
 * `toStanag` stemplet hver eneste melding `PRECEDENCE/PRIORITY` uansett innhold
 * — altså var en MEDEVAC og en SITREP formelt like hastende ut av systemet.
 *
 * ── ⚠ HVA DETTE ER, OG HVA DET IKKE ER ─────────────────────────────────────
 * `precedence.ts` forbyr eksplisitt å dikte opp TIDSFRISTER for kodeordene,
 * fordi frister er doktrine og vi HAR ikke doktrinen. Den regelen gjelder
 * fortsatt, og denne tabellen bryter den ikke: her står ingen frister.
 *
 * Det som står her er REKKEFØLGE FOR ØYET — hvilken melding som skal ha farge
 * og topplassering når to kom inn samtidig. Det er en UI-standardverdi, et
 * GULV: operatøren kan alltid heve hastegraden på sin egen melding, og en
 * mottatt melding som bærer sin egen hastegrad overstyrer tabellen.
 *
 * ÅPENT SPØRSMÅL FOR EIER: dette er vår begrunnede standard, ikke et sitat fra
 * et direktiv. Har HV/Forsvaret en fastsatt presedens per meldingstype, er
 * DETTE den ene fila som skal rettes.
 */
export const REPORT_PRECEDENCE = {
    // Egen styrke i kontakt og egen såret er de to tilfellene der minutter er
    // utfallet, og de to som ALDRI skal måtte letes etter i en logg.
    CONTACT: 'immediate',
    MEDEVAC: 'immediate',
    // CBRN rammer alle nedstrøms av vinden, ikke bare avsender — den kan ikke
    // ligge og vente på at noen ruller i loggen.
    CBRN: 'immediate',
    // Fiendtlig observasjon skal opp, men den er et grunnlag for beslutning, ikke
    // en pågående nødsituasjon.
    SALUTE: 'priority',
    // UXO er farlig, men statisk: faren flytter seg ikke mens du leser.
    UXO: 'priority',
    // Fri stridsmelding kan inneholde hva som helst, også noe som haster. Den får
    // derfor `priority` og ikke `routine` — å UNDERVURDERE en melding vi ikke har
    // lest er en verre feil enn å overvurdere den.
    MELDING: 'priority',
    // Periodisk situasjonsrapport er selve definisjonen av rutine.
    SITREP: 'routine',
};
/** Case-status — driver OODA-loopen: fra observasjon til handling til lukket. */
export const ReportStatus = z.enum(['ny', 'kvittert', 'aktiv', 'løst']);
export const STATUS_LABEL = {
    ny: 'Ny',
    kvittert: 'Kvittert',
    aktiv: 'Aktiv',
    løst: 'Løst',
};
export const STATUS_COLOR = {
    ny: '#ff5b5b',
    kvittert: '#ff8a3d',
    aktiv: '#5b9bd5',
    løst: '#4bd15b',
};
/** Ett innlegg i meldingstråden (toveis dialog rundt et case). */
export const ReportCommentSchema = z.object({
    id: z.string().max(128),
    author: z.string().max(128),
    tsMs: z.number(),
    text: z.string().max(4000),
});
/**
 * Objektet en melding HANDLER OM — ankeret som gjør at det man sier og det man
 * ser er samme sted.
 *
 * ── HVORFOR ────────────────────────────────────────────────────────────────
 * UX-gjennomgang 2026-08-18, funn **F4**, prioritet **HØY** — det eneste
 * HØY-funnet som sto åpent etter Fase 0–2:
 *
 *   «En melding i Feltveksler blir aldri et objekt på kartet, og et spor/kontakt
 *   på kartet har ingen egen samtaletråd. To parallelle virkeligheter: det man
 *   sier og det man ser møtes aldri. Konsekvens: kontekst går tapt; "hvor var det
 *   han meldte om?" krever manuell kryssjekk.»
 *
 * Rapportens Fase 3: *«Hvert spor, hver kontakt, hvert punkt har sin egen
 * samtaletråd. Å melde om fiendtlig kjøretøy skjer PÅ kjøretøyet, ikke i en
 * løsrevet kanal.»*
 *
 * ── ⚠ HVORFOR DETTE OG IKKE EN NY MELDINGSTJENESTE ─────────────────────────
 * `CombatReport` har ALT en dialogtråd (`thread`, append-only, flettet union på
 * id — se `mergeReport`), den ligger på kartet, den deles over hubben og den
 * replayes ved reload. Den mangler bare ÉN opplysning: hva den handler om.
 *
 * Å bygge et parallelt trådsystem for spor ville laget den tredje
 * meldingsvirkeligheten i et system rapporten kritiserer for å ha to. Ankeret er
 * det minste inngrepet som faktisk lukker F4.
 *
 * ⚠ VALGFRITT, OG DET SKAL DET FORBLI. En melding uten anker er ikke en feil —
 * det er en melding om noe som ikke finnes som objekt ennå, og det er den
 * vanligste meldingen av alle. Gjorde vi feltet obligatorisk, måtte operatøren
 * ha pekt på noe før hun fikk melde, og da er vi tilbake i «skjema først, kart
 * sist» (funn F3).
 */
export const ObjektAnkerSchema = z.object({
    /**
     * Hva slags objekt.
     *
     * `spor` er en sensorkontakt (`Track`), `enhet` er en plassert ORBAT-enhet, og
     * `punkt` er et navngitt punkt operatøren har satt. Slaget er med fordi id-ene
     * lever i ULIKE navnerom: `spor-4711` og enhet `HV-02-201` kan i prinsippet
     * kollidere, og en melding som havner på feil objekt er verre enn en melding
     * uten anker.
     */
    slag: z.enum(['spor', 'enhet', 'punkt']),
    id: z.string().max(128),
    /**
     * Objektets etikett DA MELDINGEN BLE SKREVET.
     *
     * ⚠ ET FROSSET SITAT, IKKE EN OPPSLAGSNØKKEL. Et spor skifter etikett når
     * fusjonen får ny informasjon, og en gammel melding skal fortsatt kunne leses
     * som «dette meldte hun om KJØRETØY 3» selv om sporet nå heter noe annet — og
     * særlig når sporet er sluppet og ikke lenger finnes å slå opp.
     */
    etikett: z.string().max(200).default(''),
});
export const CombatReportSchema = z.object({
    id: z.string(),
    type: ReportType,
    lat: z.number(),
    lon: z.number(),
    tsMs: z.number(),
    author: z.string(),
    fields: z.record(z.string().max(4000)),
    /** Case-status (default 'ny'). */
    status: ReportStatus.default('ny'),
    /** Sist endret (ms) — brukes til konfliktfri fletting (siste vinner). */
    updatedMs: z.number().default(0),
    /** Dialogtråd (append-only, flettes union på id). */
    thread: z.array(ReportCommentSchema).max(1000).default([]),
    /**
     * Objektet meldingen handler om (funn F4). Valgfritt — se `ObjektAnkerSchema`.
     */
    omObjekt: ObjektAnkerSchema.optional(),
});
/** Konfliktfri fletting av to kopier av samme melding (sanntid, samtidige svar):
 * tråd = union på kommentar-id (tidssortert); status/felt fra den nyest endrede. */
export function mergeReport(a, b) {
    const base = b.updatedMs >= a.updatedMs ? b : a;
    const byId = new Map();
    for (const c of [...a.thread, ...b.thread])
        byId.set(c.id, c);
    const thread = [...byId.values()].sort((x, y) => x.tsMs - y.tsMs);
    return { ...base, thread };
}
/** Maks meldinger prosessert per delings-event (mot oppblåsing ved fanout). */
export const MAX_REPORTS = 2000;
/** Parser et array av (upålitelige) meldinger trygt — dropper ugyldige. */
export function parseReports(raw) {
    if (!Array.isArray(raw))
        return [];
    const out = [];
    for (const item of raw.slice(0, MAX_REPORTS)) {
        const r = CombatReportSchema.safeParse(item);
        if (r.success)
            out.push(r.data);
    }
    return out;
}
/** Kort ett-linjes sammendrag til listevisning. */
export function reportSummary(r) {
    const f = r.fields;
    switch (r.type) {
        case 'MELDING':
            return [f.hvem, f.hva].filter(Boolean).join(' — ') || 'Stridsmelding';
        case 'SALUTE':
            return [f.size, f.activity, f.unit].filter(Boolean).join(' · ') || 'SALUTE';
        case 'CONTACT':
            return [f.enemy, f.action].filter(Boolean).join(' — ') || 'Kontakt';
        case 'SITREP':
            return f.situation || f.status || 'SITREP';
        case 'MEDEVAC':
            return [f.patients ? `${f.patients} pas.` : '', f.precedence].filter(Boolean).join(' · ') || 'MEDEVAC';
        case 'UXO':
            return [f.type, f.nbc ? `NBC: ${f.nbc}` : ''].filter(Boolean).join(' · ') || 'UXO';
        case 'CBRN':
            return [f.agent, f.area].filter(Boolean).join(' · ') || 'CBRN';
        default:
            return r.type;
    }
}
/**
 * Meldingene som handler om ett objekt — «tråden på objektet» (funn F4).
 *
 * ⚠ ELDSTE FØRST, motsatt av hendelsesloggen. En tråd LESES kronologisk framover:
 * du vil vite hva som ble meldt først og hva som kom etterpå. Hendelsesloggen er
 * nyeste-først fordi den svarer «hva skjer nå»; en tråd svarer «hva har skjedd
 * med denne tingen», og det er en fortelling.
 */
export function meldingerOmObjekt(reports, slag, id) {
    return reports
        .filter((r) => r.omObjekt?.slag === slag && r.omObjekt.id === id)
        .sort((a, b) => (a.tsMs - b.tsMs) || a.id.localeCompare(b.id));
}
/**
 * Kort sammendrag til objektets detaljpanel.
 *
 * ⚠ TELLER BÅDE MELDINGER OG SVAR. «2 meldinger» på et spor der det ligger to
 * meldinger med fem svar til sammen underrapporterer samtalen grovt — og en
 * operatør som ser et lavt tall åpner den ikke.
 */
export function objektTraadSammendrag(reports, slag, id) {
    const m = meldingerOmObjekt(reports, slag, id);
    const svar = m.reduce((n, r) => n + r.thread.length, 0);
    const sisteMs = m.length
        ? Math.max(...m.map((r) => Math.max(r.tsMs, ...r.thread.map((c) => c.tsMs))))
        : null;
    if (!m.length) {
        // ⚠ TOMTILSTANDEN SIER HVA OPERATØREN KAN GJØRE, ikke bare at det er tomt.
        return { meldinger: 0, svar: 0, sisteMs: null, tekst: 'Ingen meldinger om dette objektet ennå' };
    }
    const deler = [`${m.length} melding${m.length === 1 ? '' : 'er'}`];
    if (svar)
        deler.push(`${svar} svar`);
    return { meldinger: m.length, svar, sisteMs, tekst: deler.join(' · ') };
}
//# sourceMappingURL=report.js.map